<template>
  <Layout
    class="onboarding-statements"
    :title="page.title"
    :subtitle="page.subtitle"
    :style="{
      '--swipe-direction': cssSwipeDirection,
    }"
  >
    <Transition name="slide" mode="out-in">
      <div
        :key="`${triggerTransitionAnimation}`"
        class="onboarding-statements__cards-wrapper"
      >
        <div
          v-for="(statement, index) in page.content"
          :key="index"
          class="onboarding-statements__card"
          :class="{
            'onboarding-statements__card--prev': index < currentStatementIndex,
            'onboarding-statements__card--current':
              index === currentStatementIndex,
            'onboarding-statements__card--next': index > currentStatementIndex,
          }"
          :style="{
            '--background-color': colors[index % colors.length],
            '--statement-index': index - currentStatementIndex,
          }"
        >
          <p class="onboarding-statements__text">
            {{ statement }}
          </p>
          <span class="onboarding-statements__quote iconfont iquote"></span>
        </div>
      </div>
    </Transition>

    <div class="onboarding-statements__controls">
      <button
        class="onboarding-statements__button onboarding-statements__button--no"
        @click.prevent="nextStatement(false)"
      >
        <span class="iconfont iclose-bold"></span>
        No
      </button>
      <button
        class="onboarding-statements__button onboarding-statements__button--yes"
        @click.prevent="nextStatement(true)"
      >
        <span class="iconfont icheck-bold"></span>
        Yes
      </button>
    </div>
  </Layout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import type { QuestionnaireView } from '@/models/onboarding'
import type {
  StatementsChoice,
  StatementsPage,
} from '@/models/onboarding.types'

import Layout from '../Layout.vue'

const props = defineProps<{
  view: QuestionnaireView
}>()

onMounted(() => {
  // The default value of `view.currentChoice` is `undefined`.
  // The value of the choices for this page is an array, so
  // we set the initial value of `view.currentChoice` to an
  // empty array so that we can spread the current value
  // without having to check if it's an array first.
  props.view.setCurrentChoice([])
})

const page = computed(() => {
  return props.view.currentPage as StatementsPage
})

const currentChoice = computed(() => {
  return props.view.currentChoice as StatementsChoice
})

const currentStatementIndex = computed(() => {
  return props.view.currentSubpageIndex
})

const currentStatement = computed(() => {
  return page.value.content[currentStatementIndex.value]
})

const colors = ['255, 147, 88', '56, 118, 240', '81, 176, 111']

/**
 * This ref is used to trigger the <Transition> component
 * that animates the transition between cards. The actual
 * value of this ref is irrelevant, we just need to change
 * it to trigger the animation.
 */
const triggerTransitionAnimation = ref(false)

/**
 * The direction of the card swipe animation
 * when going to the next card. This value
 * is used in the CSS transitions.
 *
 * -1 = right
 * +1 = left
 */
const cssSwipeDirection = ref<-1 | 1>(1)

function nextStatement(liked: boolean): void {
  if (liked) {
    props.view.setCurrentChoice([
      ...currentChoice.value,
      currentStatement.value,
    ])

    cssSwipeDirection.value = 1
  } else {
    cssSwipeDirection.value = -1
  }

  const nextStatementExists = props.view.nextSubpage()

  if (!nextStatementExists) {
    setTimeout(() => {
      // This timeout moves this action to the end
      // of the event queue, giving time to finish
      // the swipe animation of the last card.
      props.view.goNextPage()
    }, 0)
  }

  triggerTransitionAnimation.value = !triggerTransitionAnimation.value
}
</script>

<style lang="scss" scoped>
.onboarding-statements {
  padding-bottom: var(--onboarding-padding);
  @media (max-width: $w-medium-b) {
    overflow: hidden;
  }

  :deep(.onboarding-layout__default-slot) {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__cards-wrapper {
    z-index: 1;
    display: grid;
    grid-template-areas: 'stack';
    margin: auto;
    @media (min-width: $w-pc-b) {
      margin-block: 80px;
    }
  }

  &__card {
    position: relative;
    z-index: calc(10 - var(--statement-index));
    display: flex;
    flex-direction: column;
    gap: 20px;
    align-items: center;
    justify-content: center;
    grid-area: stack;
    padding: 28px 40px;
    border-radius: 8px;
    border: 1px solid $c-border-7;
    background: $c-white;
    filter: drop-shadow(0px 7px 12px rgba($c-black-2, 0.03));
    @media (max-height: 450px) {
      padding-block: 12px;
    }
  }

  &__card--prev {
    // We use visibility instead of `display: none` so that the
    // element still takes space in the grid, which prevents
    // the other elements from being resized.
    visibility: hidden;
  }

  &__card--next {
    bottom: calc(var(--statement-index) * -22px);
    transform: scale(calc(1 - (var(--statement-index) * 0.1)));
  }

  &__quote {
    font-size: 20px;
    color: rgb(var(--background-color));
  }

  &__text {
    margin: 0;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: -0.02em;
    text-align: center;
    color: $c-black-2;
  }

  &__quote {
    width: 28px;
    height: 28px;
    font-size: 28px;
    transition: all 0.3s ease;
  }

  &__controls {
    z-index: 2;
    display: flex;
    gap: 12px;
  }

  &__button {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-height: 112px;
    border: 1px solid $c-border-2;
    border-radius: 14px;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0.02em;
    background: $c-white;
    color: $c-black-15;

    .iconfont {
      font-size: 32px;
      line-height: 1;
    }

    @media (max-height: 530px) {
      min-height: 0;
      padding: 12px;
      flex-direction: row;
      gap: 4px;

      .iconfont {
        font-size: 24px;
        width: 24px;
        height: 24px;
      }
    }

    @include hover {
      border-color: $c-black-3;
    }
  }

  &__button--no {
    .iconfont {
      color: $c-red-5;
    }
  }

  &__button--yes {
    .iconfont {
      color: $c-green-2;
    }
  }
}

.slide {
  &-enter-active,
  &-leave-active,
  &-enter-active .onboarding-statements__text,
  &-leave-active .onboarding-statements__text {
    transition: all 0.4s ease-in-out;
  }

  &-enter-active .onboarding-statements__card,
  &-leave-active .onboarding-statements__card {
    transition: all 0.3s ease;
  }

  &-leave-from {
    .onboarding-statements__card--next .onboarding-statements__text {
      opacity: 0;
    }
  }

  &-leave-to {
    .onboarding-statements__card--current {
      opacity: 0;
      translate: calc(100px * var(--swipe-direction));
      rotate: calc(5deg * var(--swipe-direction));
    }

    .onboarding-statements__card--next {
      translate: 0 -22px;
      transform: scale(calc(1 - ((var(--statement-index) - 1) * 0.1)));

      .onboarding-statements__text {
        opacity: 1;
      }
    }
  }
}
</style>
